import { useCallback, useEffect, useRef } from 'react';

/**
 * Custom hook to handle unsaved changes detection
 * @param {Object} formState - React Hook Form's formState object
 * @param {Function} onUnsavedChanges - Callback when there are unsaved changes and user tries to close
 * @param {boolean} isOpen - Whether the modal/form is open
 * @returns {Object} - Methods to handle closing with unsaved changes
 */
export function useUnsavedChanges({
  formState,
  onUnsavedChanges,
  isOpen = true
}) {
  const hasUnsavedChanges = formState?.isDirty && !formState?.isSubmitSuccessful;
  const previousHasUnsavedChanges = useRef(false);

  useEffect(() => {
    previousHasUnsavedChanges.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  // Handle closing with potential unsaved changes
  const handleClose = useCallback((originalCloseHandler) => {
    if (hasUnsavedChanges && onUnsavedChanges) {
      onUnsavedChanges({
        hasUnsavedChanges: true,
        onDiscard: originalCloseHandler,
        onCancel: () => {} // Do nothing, just close the dialog
      });
    } else {
      originalCloseHandler();
    }
  }, [hasUnsavedChanges, onUnsavedChanges]);

  // Browser beforeunload event handler for when modal is open
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isOpen && hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return event.returnValue;
      }
    };

    if (isOpen) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [isOpen, hasUnsavedChanges]);

  return {
    hasUnsavedChanges,
    handleClose
  };
}

export default useUnsavedChanges; 